import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Checkbox, Footer, Hr, Input, Radio } from "@atsent/components";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
import {
  customToastState,
  errorToastState,
  modalState,
} from "lib/store/common";
import {
  addCard,
  asyncCardData,
  cardInfoType,
  cardType,
  removeCard,
  startSubscription,
  subscriptionType,
  updateCardInfo,
} from "lib/store/billing";

import { asyncGetProfile, updatePlan } from "lib/store/user";

import { SideNLogo } from "components/template/SideNLogo";

import "./PaymentPage.scss";

const Standard = () => (
  <ul>
    <li>조회수 최대 10만건</li>
    <li>csv / slack / email 연동</li>
    <li>채널톡 연동</li>
    <li>기술지원 - 이메일/전화</li>
  </ul>
);

const Business = () => (
  <ul>
    <li>조회수 최대 40만건</li>
    <li>csv / slack / email 연동</li>
    <li>채널톡 연동</li>
    <li>기술지원 - 이메일/전화</li>
  </ul>
);

const Enterprise = () => (
  <ul>
    <li>조회수 최대 200만건</li>
    <li>csv / slack / email 연동</li>
    <li>채널톡/SNS 연동</li>
    <li>기술지원 - 담당자 지정</li>
  </ul>
);

const usePaymentInfo = () => {
  const payment = useRecoilValueLoadable(asyncCardData);
  const profile = useRecoilValueLoadable(asyncGetProfile);

  const returnVal: {
    state: any;
    username: any;
    appliedCardInfo?: cardType;
  } = {
    state: payment.state,
    username: null,
    appliedCardInfo: undefined,
  };

  if (payment.state === "hasValue" && !!payment.contents) {
    returnVal.appliedCardInfo = payment.contents;
  }

  if (profile.state === "hasValue") {
    returnVal.username = profile.contents.username;
  }

  return returnVal;
};

export const PaymentPage: React.FC = () => {
  const navigate = useNavigate();

  const [select, setSelect] = useState(0);
  const [checked, setChecked] = useState(false);

  const refresh = useSetRecoilState(updatePlan);

  const setModal = useSetRecoilState(modalState);
  const setErrorToast = useSetRecoilState(errorToastState);
  const setToast = useSetRecoilState(customToastState);
  const refreshCardData = useSetRecoilState(updateCardInfo);

  const { username, appliedCardInfo } = usePaymentInfo();

  const [cardInfo, setCardInfo] = useState<cardInfoType>({
    card_number: "",
    expiry: "",
    birth: "",
    pwd_2digit: "",
    customer_uid: "",
  });

  const [subscriptionInfo, setSubscriptionInfo] = useState<subscriptionType>({
    buyer_addr: "",
    buyer_email: "",
    buyer_name: "",
    plan: "STANDARD",
  });

  const handleAddCard = async () => {
    try {
      await addCard(cardInfo);
      await setToast({
        type: "success",
        text: "카드 등록이 완료되었습니다..",
      });
      await refreshCardData(new Date());
    } catch (e) {
      setErrorToast(e);
    }
  };

  const handleStartSubscription = async () => {
    if (!appliedCardInfo) {
      setToast({ text: "카드 정보 불러오기에 실패했습니다.", type: "error" });
      return;
    }

    try {
      await startSubscription(subscriptionInfo);
      await setToast({ text: "구독에 성공하였습니다", type: "success" });
      await refresh(new Date());
      await navigate("/");
    } catch (e) {
      setErrorToast(e);
    }
  };

  return (
    <SideNLogo cx={"PaymentPage"}>
      <div className="select-box">
        <div className={`select _${select === 0}`} onClick={() => setSelect(0)}>
          <span className="round">1</span>
          <span>Select a plan</span>
        </div>
        <div className={`select _${select === 1}`} onClick={() => setSelect(1)}>
          <span className="round">2</span>
          <span>Set up payment</span>
        </div>
      </div>
      {select === 0 ? (
        <div className="plan">
          <div className={"choose"}>Select a Plan</div>
          <div className={"desc"}>
            You only pay for users who edit atSent Project
          </div>
          <div className="Radio-box">
            <Radio
              name={"payment"}
              onChange={() => {
                setSubscriptionInfo((_) => ({ ..._, plan: "STANDARD" }));
              }}
              checked={subscriptionInfo.plan === "STANDARD"}
            >
              <div className="title">Standard</div>
              <div className="price">
                <strong>₩99,000</strong> / 월
              </div>
              <Standard />
            </Radio>
            <Radio
              name={"payment"}
              onChange={() =>
                setSubscriptionInfo((_) => ({ ..._, plan: "BUSINESS" }))
              }
              checked={subscriptionInfo.plan === "BUSINESS"}
            >
              <div className="title">Business</div>
              <div className="price">
                <strong>₩399,000</strong> / 월
              </div>
              <Business />
            </Radio>
            <Radio
              name={"payment"}
              onChange={() =>
                setSubscriptionInfo((_) => ({ ..._, plan: "ENTERPRISE" }))
              }
              checked={subscriptionInfo.plan === "ENTERPRISE"}
            >
              <div className="title">Enterprise</div>
              <div className="price">
                <strong>₩1,599,000</strong> / 월
              </div>
              <Enterprise />
            </Radio>
          </div>
          <div className={"tax"}>*부가세 제외</div>
          <Button
            click={() => {
              setSelect(1);
            }}
            size={"large"}
            cx={"center"}
          >
            결제하기
          </Button>
        </div>
      ) : (
        <div className="payment">
          <div className={"choose"}>Set up payment</div>
          <div className="l">
            <div className="title">Payment Method</div>
            {!!appliedCardInfo && (
              <>
                <Hr />
                <div className="card-info">
                  <span>
                    {appliedCardInfo.card_data.card_name}
                    {appliedCardInfo.card_data.card_number}
                  </span>
                  <span
                    className={"X"}
                    onClick={async () => {
                      await removeCard();
                      await refreshCardData(new Date());
                      await setToast({
                        type: "success",
                        text: "카드 삭제가 완료되었습니다...",
                      });
                    }}
                  >
                    X
                  </span>
                </div>
                <Hr />
                <Input
                  size={"small"}
                  label={"구매자 이름"}
                  handleOnChange={(e) =>
                    setSubscriptionInfo((_) => ({
                      ..._,
                      buyer_name: e.target.value,
                    }))
                  }
                />
                <Input
                  size={"small"}
                  label={"구매자 이메일"}
                  handleOnChange={(e) =>
                    setSubscriptionInfo((_) => ({
                      ..._,
                      buyer_email: e.target.value,
                    }))
                  }
                />
                <Hr />
                <Input
                  size={"small"}
                  label={"구매자 주소"}
                  handleOnChange={(e) =>
                    setSubscriptionInfo((_) => ({
                      ..._,
                      buyer_addr: e.target.value,
                    }))
                  }
                />
                <Hr />
                <Checkbox
                  onChange={(e) => setChecked((_) => e.target.checked)}
                  label={
                    <>
                      <span className={"small"}>
                        서비스 이용 약관에 동의합니다.
                      </span>
                    </>
                  }
                />
                <Hr />
                <Button
                  size={"large"}
                  cx={"center"}
                  disabled={!checked}
                  click={handleStartSubscription}
                >
                  결제 진행
                </Button>
              </>
            )}
            {!appliedCardInfo && (
              <>
                <Input
                  size={"small"}
                  label={"카드 번호"}
                  type={"password"}
                  placeholder={"XXXXXXXXXXXX"}
                  maxlength={16}
                  passwordVisible
                  handleOnChange={(e) => {
                    setCardInfo((_) => ({
                      ..._,
                      card_number: e.target.value,
                      customer_uid: `${username}_${e.target.value.slice(
                        e.target.value.length - 4
                      )}`,
                    }));
                  }}
                />
                <Input
                  size={"small"}
                  label={"카드 유효 기간"}
                  type={"password"}
                  placeholder={"YYYYMM"}
                  maxlength={6}
                  passwordVisible
                  handleOnChange={(e) =>
                    setCardInfo((_) => ({ ..._, expiry: e.target.value }))
                  }
                />
                <Input
                  size={"small"}
                  label={"생년월일/사업자번호"}
                  type={"password"}
                  placeholder={"YYMMDD"}
                  passwordVisible
                  maxlength={10}
                  handleOnChange={(e) =>
                    setCardInfo((_) => ({ ..._, birth: e.target.value }))
                  }
                />
                <Input
                  size={"small"}
                  label={"카드 비밀번호 앞 두자리"}
                  type={"password"}
                  placeholder={"XX"}
                  passwordVisible
                  maxlength={2}
                  handleOnChange={(e) =>
                    setCardInfo((_) => ({ ..._, pwd_2digit: e.target.value }))
                  }
                />
                <br/>
                {/*<hr/>*/}
                <br/>
                <br/>
                {/*<Input size={"small"} label={"Detail Address"} />*/}
                <Button
                  size={"large"}
                  cx={"center"}
                  click={() => handleAddCard()}
                >
                  카드 등록
                </Button>
              </>
            )}
          </div>
          <div className="r">
            <span>Your Bill Summary</span>
            <Hr />
            {subscriptionInfo.plan === "STANDARD" && <Standard />}
            {subscriptionInfo.plan === "BUSINESS" && <Business />}
            {subscriptionInfo.plan === "ENTERPRISE" && <Enterprise />}
            <Hr />
            <div className={"sum"}>
              <span>총 금액</span>
              {subscriptionInfo.plan === "STANDARD" && <span>₩99,000/월</span>}
              {subscriptionInfo.plan === "BUSINESS" && <span>₩399,000/월</span>}
              {subscriptionInfo.plan === "ENTERPRISE" && (
                <span>₩1,599,000/월</span>
              )}
            </div>
            <Hr />
            <span className={"refund"}>* 부가세 별도</span>
            <br />
            <span className={"refund"}>
              * 환불시 사용량을 일 별 요금으로 산출, 선결제한 월 요금에서 제하여
              환불해 드립니다.
            </span>
          </div>
        </div>
      )}
      <Footer
        clickPrivacyPolicy={() => setModal((_) => ({ ..._, pp: true }))}
        clickTerms={() => setModal((_) => ({ ..._, term: true }))}
      />
    </SideNLogo>
  );
};
